import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'
import type { VideoBriefSlide, VideoBriefSlideSource, VideoBriefStoryboard } from '@/types/videoBrief'
import { formatClock } from './clock'

/** Source types the PDF viewer can open at a page. Others are shown as text only. */
const PAGE_VIEWER_TYPES = new Set(['document', 'pnid', 'pfd', 'sld'])

interface StoryboardPanelProps {
  storyboard: VideoBriefStoryboard | null
  activeIndex: number
  onSeek: (slide: VideoBriefSlide) => void
}

function sourceHref(source: VideoBriefSlideSource, sourceType: string | undefined): string | null {
  if (!sourceType || !PAGE_VIEWER_TYPES.has(sourceType)) return null
  const page = source.page ? `?page=${source.page}` : ''
  return `/document/${source.document_id}${page}`
}

/**
 * Every slide of a brief with the file and page it was cut from. Clicking a
 * row seeks the player; the source link opens the file at that page, which is
 * what "every slide shows its reference" means outside the frame.
 */
export default function StoryboardPanel({ storyboard, activeIndex, onSeek }: StoryboardPanelProps) {
  const { t } = useTranslation()

  if (!storyboard || storyboard.slides.length === 0) {
    return (
      <p className="text-sm text-muted-foreground p-3">
        {t('document_viewer:video_brief.no_storyboard', {
          defaultValue: 'No slide list is available for this video.',
        })}
      </p>
    )
  }

  const typeById = new Map(storyboard.sources.map((s) => [s.document_id, s.type]))

  return (
    <ol className="divide-y" data-testid="storyboard-panel">
      {storyboard.slides.map((slide, i) => {
        const href = sourceHref(slide.source, typeById.get(slide.source.document_id))
        const sourceLabel = slide.source.page
          ? t('document_viewer:video_brief.source_page', {
              defaultValue: '{{name}} · page {{page}}',
              name: slide.source.name,
              page: slide.source.page,
            })
          : slide.source.name
        return (
          <li
            key={slide.index}
            className={clsx('px-3 py-2', i === activeIndex && 'bg-accent')}
            aria-current={i === activeIndex ? 'true' : undefined}
          >
            <button
              type="button"
              onClick={() => onSeek(slide)}
              className="w-full text-left flex gap-3 items-baseline"
            >
              <span className="font-mono text-xs text-muted-foreground w-10 shrink-0">
                {formatClock(slide.start_seconds)}
              </span>
              <span className="text-sm font-medium truncate">{slide.title}</span>
            </button>
            <div className="pl-13 ml-[3.25rem] mt-0.5 text-xs text-muted-foreground flex items-center gap-1 min-w-0">
              <span className="truncate">{sourceLabel}</span>
              {href && (
                <Link
                  to={href}
                  className="inline-flex items-center gap-0.5 hover:text-foreground shrink-0"
                  title={t('document_viewer:video_brief.open_source', { defaultValue: 'Open source' })}
                >
                  <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
